/* eslint-disable react-refresh/only-export-components */
/**
 * AuthCleanupContext
 *
 * Coordinates cleanup across all contexts when user logs out.
 * This ensures that user-specific data is properly cleared to prevent:
 * - Data leaks between users
 * - Stale data after logout
 * - Memory leaks from orphaned subscriptions
 *
 * Usage:
 * 1. Wrap your app with AuthCleanupProvider (inside AuthProvider)
 * 2. In each context that needs cleanup, use useAuthCleanup() to register a cleanup function
 * 3. Call executeAllCleanups() from AuthContext when user logs out
 */

import {
  createContext,
  useContext,
  useRef,
  useCallback,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';

type CleanupFn = () => void | Promise<void>;

interface AuthCleanupContextValue {
  /**
   * Register a cleanup function to be called on logout.
   * @param id - Unique identifier for this cleanup (e.g., 'dm-context')
   * @param fn - Cleanup function to execute
   */
  registerCleanup: (id: string, fn: CleanupFn) => void;

  /**
   * Unregister a cleanup function.
   * Call this when a component/context unmounts.
   * @param id - The same identifier used in registerCleanup
   */
  unregisterCleanup: (id: string) => void;

  /**
   * Execute all registered cleanup functions.
   * Called by AuthContext when user logs out.
   */
  executeAllCleanups: () => Promise<void>;
}

const AuthCleanupContext = createContext<AuthCleanupContextValue | null>(null);

interface AuthCleanupProviderProps {
  children: ReactNode;
}

export function AuthCleanupProvider({ children }: AuthCleanupProviderProps) {
  // Use a ref to store cleanup functions to avoid re-renders when registering
  const cleanupFnsRef = useRef<Map<string, CleanupFn>>(new Map());

  const registerCleanup = useCallback((id: string, fn: CleanupFn) => {
    cleanupFnsRef.current.set(id, fn);
  }, []);

  const unregisterCleanup = useCallback((id: string) => {
    cleanupFnsRef.current.delete(id);
  }, []);

  const executeAllCleanups = useCallback(async () => {
    const cleanupPromises: Promise<void>[] = [];

    for (const [id, fn] of cleanupFnsRef.current.entries()) {
      try {
        const result = fn();
        if (result instanceof Promise) {
          cleanupPromises.push(
            result.catch((error) => {
              console.warn(`Cleanup error for ${id}:`, error);
            })
          );
        }
      } catch (error) {
        console.warn(`Cleanup error for ${id}:`, error);
      }
    }

    // Wait for all async cleanups to complete
    if (cleanupPromises.length > 0) {
      await Promise.all(cleanupPromises);
    }

    // NOTE: We intentionally do NOT clear the cleanup map here.
    // Providers (AudioProvider, DMProvider, etc.) never unmount because they
    // wrap the entire app tree, so their registerCleanup effects only run once.
    // If we cleared the map, subsequent logouts would skip all cleanup functions.
  }, []);

  const value: AuthCleanupContextValue = useMemo(
    () => ({
      registerCleanup,
      unregisterCleanup,
      executeAllCleanups,
    }),
    [registerCleanup, unregisterCleanup, executeAllCleanups]
  );

  return <AuthCleanupContext.Provider value={value}>{children}</AuthCleanupContext.Provider>;
}

/**
 * Hook to access auth cleanup registration.
 * Use this in contexts that need to clear user-specific data on logout.
 */
export function useAuthCleanup(): AuthCleanupContextValue {
  const context = useContext(AuthCleanupContext);
  if (!context) {
    // Return no-op functions if not wrapped in provider
    // This allows contexts to work independently during testing
    return {
      registerCleanup: () => {},
      unregisterCleanup: () => {},
      executeAllCleanups: async () => {},
    };
  }
  return context;
}

/**
 * Hook to register a cleanup function that runs on logout.
 * Automatically unregisters on unmount to prevent memory leaks.
 *
 * @param id - Unique identifier for this cleanup
 * @param cleanupFn - Function to call on logout
 */
export function useRegisterAuthCleanup(id: string, cleanupFn: CleanupFn): void {
  const { registerCleanup, unregisterCleanup } = useAuthCleanup();

  // Register cleanup on mount, unregister on unmount
  // Using a ref to avoid re-registering on every render
  const cleanupFnRef = useRef(cleanupFn);
  cleanupFnRef.current = cleanupFn;

  // Register with a wrapper that always calls the latest cleanup function
  const wrappedCleanup = useCallback(() => {
    return cleanupFnRef.current();
  }, []);

  // Register on mount, unregister on unmount to prevent memory leaks
  useEffect(() => {
    registerCleanup(id, wrappedCleanup);
    return () => {
      unregisterCleanup(id);
    };
  }, [id, registerCleanup, unregisterCleanup, wrappedCleanup]);
}
